import { Api } from "services/api/api.service";
import type {
  StaffItem,
  StaffSortBy,
} from "services/api/company/staff.service";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { isOk } from "utils/result";
import { useCompanyFilterOptions } from "web-apps/company/components/common/hooks/useCompanyFilterOptions";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { parseStoredPreferences } from "web-apps/company/stores/preferences.storage.logic";
import type { StaffGroupByOption } from "./components/optionsPanel/staffOptionsPanel.component";

interface StaffSection {
  id: string;
  label: string;
  items: StaffItem[];
  unitName: string | null;
  teamName: string | null;
}

interface UseStaffLogicResult {
  companyId: number | undefined;
  selectedWorkGroupIds: string[];
  selectedPersonIds: string[];
  isOptionsOpen: boolean;
  isAddStaffPanelOpen: boolean;
  selectedWorkerId: number | null;
  groupByOption: StaffGroupByOption;
  isLoadingStaff: boolean;
  isLoadingMoreStaff: boolean;
  staffLoadError: boolean;
  isEmptyState: boolean;
  sections: StaffSection[];
  hasMoreStaff: boolean;
  loadMoreRef: React.MutableRefObject<HTMLDivElement | null>;
  filterOptions: ReturnType<typeof useCompanyFilterOptions>;
  setSelectedWorkGroupIds: React.Dispatch<React.SetStateAction<string[]>>;
  setSelectedPersonIds: React.Dispatch<React.SetStateAction<string[]>>;
  setIsOptionsOpen: (value: boolean) => void;
  setIsAddStaffPanelOpen: (value: boolean) => void;
  setGroupByOption: (value: StaffGroupByOption) => void;
  closeWorkerProfile: () => void;
  openWorkerProfile: (workerId: number) => void;
  refreshStaff: () => void;
  handleWorkerDeleted: (deletedWorkerId: number) => void;
}

const DEFAULT_PAGE = 0;
const DEFAULT_PAGE_SIZE = 50;
const STAFF_DEFAULT_GROUP_BY: StaffGroupByOption = "person";

//save preferences for grouping
const staffPreferencesStorageKey = (companyId: number): string =>
  `PERSISTED::StaffPreferences::${companyId}`;

const isStaffGroupByOption = (value: string): value is StaffGroupByOption =>
  value === "person" || value === "team";

interface StaffPreferences {
  groupByOption: StaffGroupByOption;
}

const isStaffPreferences = (value: object): value is StaffPreferences => {
  const groupByOption = Reflect.get(value, "groupByOption");
  if (typeof groupByOption !== "string") return false;
  return isStaffGroupByOption(groupByOption);
};

const toPositiveIntegerIds = (values: string[]): number[] => {
  const uniqueIds = new Set<number>();
  values.forEach((value) => {
    if (!/^\d+$/.test(value)) return;
    const parsedValue = Number(value);
    if (!Number.isSafeInteger(parsedValue) || parsedValue <= 0) return;
    uniqueIds.add(parsedValue);
  });
  return Array.from(uniqueIds);
};

const mergeStaffItemsByWorkerId = (
  previousItems: StaffItem[],
  nextItems: StaffItem[],
): StaffItem[] => {
  const mergedItems = new Map<number, StaffItem>();
  previousItems.forEach((item) => {
    mergedItems.set(item.workerId, item);
  });
  nextItems.forEach((item) => {
    mergedItems.set(item.workerId, item);
  });
  return Array.from(mergedItems.values());
};

const buildPersonSections = (
  staffItems: StaffItem[],
  translate: (key: string) => string,
): StaffSection[] => {
  return [
    {
      id: "person",
      label: translate("Staff.People"),
      items: staffItems,
      unitName: null,
      teamName: null,
    },
  ];
};

interface StaffFallbackLabels {
  unknownUnit: string;
  unassignedTeam: string;
}

const getStaffFallbackLabels = (
  translate: (key: string) => string,
): StaffFallbackLabels => {
  return {
    unknownUnit: translate("Staff.UnknownUnit"),
    unassignedTeam: translate("Staff.UnassignedTeam"),
  };
};

const appendUnassignedSectionItem = (
  grouped: Map<string, StaffSection>,
  sectionWorkerIds: Map<string, Set<number>>,
  item: StaffItem,
  labels: StaffFallbackLabels,
): void => {
  const key = "unassigned";
  const existing = grouped.get(key);
  const nextLabel = `${labels.unknownUnit} ${labels.unassignedTeam}`;
  if (!existing) {
    grouped.set(key, {
      id: key,
      label: nextLabel,
      items: [item],
      unitName: labels.unknownUnit,
      teamName: labels.unassignedTeam,
    });
    sectionWorkerIds.set(key, new Set([item.workerId]));
    return;
  }

  const workerIdSet = sectionWorkerIds.get(key) ?? new Set<number>();
  if (workerIdSet.has(item.workerId)) return;
  workerIdSet.add(item.workerId);
  sectionWorkerIds.set(key, workerIdSet);
  existing.items.push(item);
};

const appendTeamSectionItem = (
  grouped: Map<string, StaffSection>,
  sectionWorkerIds: Map<string, Set<number>>,
  item: StaffItem,
  workGroup: StaffItem["workGroups"][number],
  labels: StaffFallbackLabels,
): void => {
  const key = `${workGroup.companyUnitId}-${workGroup.workGroupId}`;
  const unitName =
    (workGroup.companyUnitName ?? "").trim() || labels.unknownUnit;
  const teamName =
    (workGroup.workGroupName ?? "").trim() || labels.unassignedTeam;
  const nextLabel = `${unitName} ${teamName}`;
  const existing = grouped.get(key);
  if (!existing) {
    grouped.set(key, {
      id: key,
      label: nextLabel,
      items: [item],
      unitName,
      teamName,
    });
    sectionWorkerIds.set(key, new Set([item.workerId]));
    return;
  }

  const workerIdSet = sectionWorkerIds.get(key) ?? new Set<number>();
  if (workerIdSet.has(item.workerId)) return;
  workerIdSet.add(item.workerId);
  sectionWorkerIds.set(key, workerIdSet);
  existing.items.push(item);
};

const buildTeamSections = (
  staffItems: StaffItem[],
  selectedTeamIdSet: Set<string>,
  translate: (key: string) => string,
): StaffSection[] => {
  const grouped = new Map<string, StaffSection>();
  const sectionWorkerIds = new Map<string, Set<number>>();
  const labels = getStaffFallbackLabels(translate);

  staffItems.forEach((item) => {
    const workGroupsToRender =
      selectedTeamIdSet.size === 0
        ? item.workGroups
        : item.workGroups.filter((workGroup) =>
            selectedTeamIdSet.has(String(workGroup.workGroupId)),
          );
    const uniqueWorkGroups = Array.from(
      new Map(
        workGroupsToRender.map((workGroup) => [
          `${workGroup.companyUnitId}-${workGroup.workGroupId}`,
          workGroup,
        ]),
      ).values(),
    );

    if (uniqueWorkGroups.length === 0) {
      if (
        selectedTeamIdSet.size > 0 &&
        (item.workGroupId == null ||
          !selectedTeamIdSet.has(String(item.workGroupId)))
      ) {
        return;
      }
      appendUnassignedSectionItem(grouped, sectionWorkerIds, item, labels);
      return;
    }

    uniqueWorkGroups.forEach((workGroup) => {
      appendTeamSectionItem(grouped, sectionWorkerIds, item, workGroup, labels);
    });
  });

  return Array.from(grouped.values());
};

/**
 * Handles use staff logic.
 */
export const useStaffLogic = (): UseStaffLogicResult => {
  const { t } = useTranslation();
  const [companyState] = useCompanyStore();
  const companyId = companyState.company?.id;
  const [selectedWorkGroupIds, setSelectedWorkGroupIds] = useState<string[]>(
    [],
  );
  const [selectedPersonIds, setSelectedPersonIds] = useState<string[]>([]);
  const [isOptionsOpen, setIsOptionsOpen] = useState(false);
  const [isAddStaffPanelOpen, setIsAddStaffPanelOpen] = useState(false);
  const [selectedWorkerId, setSelectedWorkerId] = useState<number | null>(null);
  const [groupByOption, setGroupByOption] = useState<StaffGroupByOption>(
    STAFF_DEFAULT_GROUP_BY,
  );
  const [hydratedGroupByCompanyId, setHydratedGroupByCompanyId] = useState<
    number | null
  >(null);
  const [page, setPage] = useState<number>(DEFAULT_PAGE);
  const [hasMoreStaff, setHasMoreStaff] = useState(false);
  const [staffItems, setStaffItems] = useState<StaffItem[]>([]);
  const [isLoadingStaff, setIsLoadingStaff] = useState(false);
  const [isLoadingMoreStaff, setIsLoadingMoreStaff] = useState(false);
  const [staffLoadError, setStaffLoadError] = useState(false);
  const [staffRefreshVersion, setStaffRefreshVersion] = useState(0);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const latestStaffRequestIdRef = useRef(0);
  const wasLoadMoreIntersectingRef = useRef(false);
  const staffItemsCountRef = useRef(0);
  const filterOptions = useCompanyFilterOptions({
    companyId,
    setSelectedWorkGroupIds,
  });

  useEffect(() => {
    if (!companyId) {
      setHydratedGroupByCompanyId(null);
      return;
    }

    setHydratedGroupByCompanyId(null);
    const raw = localStorage.getItem(staffPreferencesStorageKey(companyId));
    if (!raw) {
      setGroupByOption(STAFF_DEFAULT_GROUP_BY);
      setHydratedGroupByCompanyId(companyId);
      return;
    }

    const parsedPreferences = parseStoredPreferences(
      raw,
      (value): value is StaffPreferences =>
        typeof value === "object" &&
        value !== null &&
        isStaffPreferences(value),
    );
    if (!isOk(parsedPreferences)) {
      setGroupByOption(STAFF_DEFAULT_GROUP_BY);
      setHydratedGroupByCompanyId(companyId);
      return;
    }

    setGroupByOption(parsedPreferences.value.groupByOption);
    setHydratedGroupByCompanyId(companyId);
  }, [companyId]);

  useEffect(() => {
    if (!companyId || hydratedGroupByCompanyId !== companyId) return;
    localStorage.setItem(
      staffPreferencesStorageKey(companyId),
      JSON.stringify({ groupByOption }),
    );
  }, [companyId, groupByOption, hydratedGroupByCompanyId]);

  const workGroupIds = useMemo<number[]>(
    () => toPositiveIntegerIds(selectedWorkGroupIds),
    [selectedWorkGroupIds],
  );
  const workerIds = useMemo<number[]>(
    () => toPositiveIntegerIds(selectedPersonIds),
    [selectedPersonIds],
  );
  const sortBy = useMemo<StaffSortBy>(
    () => (groupByOption === "team" ? "workGroup" : "worker"),
    [groupByOption],
  );
  const isPreferencesHydrated = Boolean(
    companyId != null && hydratedGroupByCompanyId === companyId,
  );

  useEffect(() => {
    staffItemsCountRef.current = staffItems.length;
  }, [staffItems.length]);

  useEffect(() => {
    if (!isPreferencesHydrated) return;
    setPage(DEFAULT_PAGE);
    setHasMoreStaff(true);
    setStaffLoadError(false);
    setStaffItems([]);
    wasLoadMoreIntersectingRef.current = false;
  }, [
    isPreferencesHydrated,
    companyId,
    sortBy,
    workGroupIds,
    workerIds,
    staffRefreshVersion,
  ]);

  useEffect(() => {
    if (!companyId) {
      setStaffItems([]);
      setIsLoadingStaff(false);
      setIsLoadingMoreStaff(false);
      setStaffLoadError(false);
      setHasMoreStaff(false);
      return;
    }
    if (!isPreferencesHydrated) return;

    const requestId = latestStaffRequestIdRef.current + 1;
    latestStaffRequestIdRef.current = requestId;
    let isCancelled = false;
    const isAppending = page > 0;

    if (isAppending) {
      setIsLoadingMoreStaff(true);
    } else {
      setIsLoadingStaff(true);
      setStaffLoadError(false);
    }

    Api()
      .company.staff.getStaff(companyId, {
        page,
        pageSize: DEFAULT_PAGE_SIZE,
        sortBy,
        status: "All",
        workGroupIds,
        workerIds,
      })
      .then((response) => {
        if (isCancelled || latestStaffRequestIdRef.current !== requestId)
          return;
        const nextItems = response.data.items ?? [];
        const total = response.data.total;
        setStaffItems((previousItems) => {
          const nextMergedItems = isAppending
            ? mergeStaffItemsByWorkerId(previousItems, nextItems)
            : nextItems;
          const hasAppendedNewItems =
            !isAppending || nextMergedItems.length > previousItems.length;
          const hasMoreByTotal = nextMergedItems.length < total;
          setHasMoreStaff(hasMoreByTotal && hasAppendedNewItems);
          return nextMergedItems;
        });
      })
      .catch((error: unknown) => {
        if (isCancelled || latestStaffRequestIdRef.current !== requestId)
          return;
        setHasMoreStaff(false);
        if (error instanceof Error && error.name === "AbortError") return;
        if (!isAppending || staffItemsCountRef.current === 0) {
          setStaffLoadError(true);
          setStaffItems([]);
        }
      })
      .finally(() => {
        if (isCancelled || latestStaffRequestIdRef.current !== requestId)
          return;
        if (isAppending) {
          wasLoadMoreIntersectingRef.current = false;
          setIsLoadingMoreStaff(false);
          return;
        }
        setIsLoadingStaff(false);
      });

    return () => {
      isCancelled = true;
    };
  }, [companyId, isPreferencesHydrated, page, sortBy, workGroupIds, workerIds]);

  const handleLoadMore = useCallback((): void => {
    if (!companyId) return;
    if (isLoadingStaff || isLoadingMoreStaff) return;
    if (!hasMoreStaff) return;
    setPage((previousPage) => previousPage + 1);
  }, [companyId, hasMoreStaff, isLoadingMoreStaff, isLoadingStaff]);

  useEffect(() => {
    if (!loadMoreRef.current) return;
    if (!hasMoreStaff || isLoadingStaff || isLoadingMoreStaff) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const isIntersecting = Boolean(entries[0]?.isIntersecting);
        if (isIntersecting && !wasLoadMoreIntersectingRef.current) {
          handleLoadMore();
        }
        wasLoadMoreIntersectingRef.current = isIntersecting;
      },
      { root: null, rootMargin: "200px", threshold: 0 },
    );

    observer.observe(loadMoreRef.current);
    return () => observer.disconnect();
  }, [handleLoadMore, hasMoreStaff, isLoadingMoreStaff, isLoadingStaff]);

  const selectedTeamIdSet = useMemo(
    () => new Set(workGroupIds.map((value) => String(value))),
    [workGroupIds],
  );

  const sections = useMemo<StaffSection[]>(() => {
    if (groupByOption === "person") return buildPersonSections(staffItems, t);
    return buildTeamSections(staffItems, selectedTeamIdSet, t);
  }, [groupByOption, selectedTeamIdSet, staffItems, t]);

  const isEmptyState =
    !isLoadingStaff && !staffLoadError && sections.length > 0
      ? sections.every((section) => section.items.length === 0)
      : !isLoadingStaff && !staffLoadError && sections.length === 0;

  const openWorkerProfile = useCallback((workerId: number): void => {
    setSelectedWorkerId(workerId);
  }, []);

  const closeWorkerProfile = useCallback((): void => {
    setSelectedWorkerId(null);
  }, []);

  const refreshStaff = useCallback((): void => {
    setStaffRefreshVersion((previousValue) => previousValue + 1);
  }, []);

  const handleWorkerDeleted = useCallback((deletedWorkerId: number): void => {
    setSelectedPersonIds((previousValue) =>
      previousValue.filter((value) => Number(value) !== deletedWorkerId),
    );
    setStaffItems((previousItems) =>
      previousItems.filter((item) => item.workerId !== deletedWorkerId),
    );
    setStaffRefreshVersion((previousValue) => previousValue + 1);
  }, []);

  return {
    companyId,
    selectedWorkGroupIds,
    selectedPersonIds,
    isOptionsOpen,
    isAddStaffPanelOpen,
    selectedWorkerId,
    groupByOption,
    isLoadingStaff,
    isLoadingMoreStaff,
    staffLoadError,
    isEmptyState,
    sections,
    hasMoreStaff,
    loadMoreRef,
    filterOptions,
    setSelectedWorkGroupIds,
    setSelectedPersonIds,
    setIsOptionsOpen,
    setIsAddStaffPanelOpen,
    setGroupByOption,
    closeWorkerProfile,
    openWorkerProfile,
    refreshStaff,
    handleWorkerDeleted,
  };
};
